import React, { useState, useEffect, useRef, memo } from 'react'
import {
  Dropdown,
  Button,
  Popover,
  Menu,
  Spin,
  Icon,
  Tooltip,
  Progress
} from 'antd'
import VideoPlayer from './VideoPlayer'
import CommentInput from '../../../CommentInput'
import TransitionSelector from '../Transition/TransitionSelector'
import TransitionDisplay from '../Transition/TransitionDisplay'
import VideoHistoryModal from '../VideoHistoryModal'
import {
  DownOutlined,
  ReloadOutlined,
  SettingOutlined,
  UploadOutlined
} from '@ant-design/icons'
import EntitySelectorList from '../../../EntitySelectorList'

const GeneratingText = () => {
  const [dots, setDots] = useState('')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'))
    }, 500)
    return () => clearInterval(interval)
  }, [])

  return <div style={{ marginTop: 8, fontWeight: 500 }}>Generating{dots}</div>
}

const Shot = ({
  shot,
  sceneId,
  index,
  isLastShot,
  formData,
  transitions,
  handleRegenerateVideo,
  setScenes,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  isGeneratingAll = false,
  videoGenerating = [],
  videoProvider,
  DragHandle,
  updateShotDataRef
}) => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const videoWrapperRef = useRef(null)

  const isVideoGenerating = videoGenerating.some(
    status => status.sceneId === sceneId && status.shotId === shot.id
  )

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true)
        } else {
          setHasBeenVisible(false)
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    )

    if (videoWrapperRef.current) {
      observer.observe(videoWrapperRef.current)
    }

    return () => {
      if (videoWrapperRef.current) {
        observer.unobserve(videoWrapperRef.current)
      }
    }
  }, [])

  const isError = shot && shot.errorGeneratingVideo

  // Memoize video source to prevent VideoPlayer re-renders
  const videoSrc = shot.vidSrc
  const imageSrc = shot.imageSrc

  return (
    <React.Fragment>
      <div
        className={`shot-card`}
        style={{
          ...(!isVideoGenerating && isError
            ? { border: '1px solid #ff4d4f' }
            : {})
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h3
            className="shot-title"
            style={{ margin: 0, display: 'flex', alignItems: 'center' }}
          >
            {DragHandle ? <DragHandle /> : null}
            Shot {index + 1}
          </h3>
        </div>
        <div
          className="video-wrapper"
          ref={videoWrapperRef}
          style={
            isError
              ? {
                  border: '1px solid #ff4d4f',
                  position: 'relative'
                }
              : { position: 'relative' }
          }
        >
          {videoSrc ? (
            <VideoPlayer url={videoSrc} isVisible={hasBeenVisible} />
          ) : (
            <img
              src={imageSrc}
              width="100%"
              height="250px"
              style={{
                objectFit: 'contain',
                filter: isError ? 'brightness(0.5)' : 'none'
              }}
            />
          )}
          {!isVideoGenerating && isError && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: '#fff',
                border: '1px solid #ff4d4f',
                color: '#ff4d4f',
                borderRadius: 4,
                padding: '2px 6px',
                fontSize: 12,
                fontWeight: 600,
                zIndex: 10
              }}
            >
              Task failed. Please regenerate.
            </div>
          )}
          {isVideoGenerating && (
            <div className="video-generating-overlay">
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={shot.progress ? parseInt(shot.progress) : 0}
                  status="active"
                  width={60}
                  strokeColor="#1890ff"
                  trailColor="rgba(255,255,255,0.3)"
                  format={percent => (
                    <span style={{ color: 'white', fontSize: '12px' }}>
                      {percent}%
                    </span>
                  )}
                />
                <GeneratingText />
              </div>
            </div>
          )}
          {/* Video History Button */}
          {shot.videos_history && shot.videos_history.length > 0 && (
            <div
              style={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}
            >
              <Tooltip title={`View History (${shot.videos_history.length})`}>
                <Button
                  shape="round"
                  onClick={() => setHistoryModalVisible(true)}
                  disabled={isVideoGenerating}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  <Icon type="history" />
                </Button>
              </Tooltip>
            </div>
          )}
        </div>

        {/* <EntitySelectorList
          activePrompt={
            shot.video_prompt && shot.video_prompt.length < 1000
              ? shot.video_prompt
              : shot.initial_image_prompt
          }
          onUpdatePrompt={newText =>
            updateShotDataRef.current(sceneId, shot.id, 'video_prompt', newText)
          }
          formData={formData}
          disabled={isVideoGenerating}
        /> */}

        <CommentInput
          characters={formData.characterList}
          text={
            shot.video_prompt && shot.video_prompt.length < 1000
              ? shot.video_prompt
              : shot.initial_image_prompt
          }
          placeholder="Write your video prompt..."
          disabled={isVideoGenerating}
          onChange={value =>
            updateShotDataRef.current(
              sceneId,
              shot.id,
              shot.video_prompt && shot.video_prompt.length < 1000
                ? 'video_prompt'
                : 'initial_image_prompt',
              value
            )
          }
        />

        {videoProvider === 'wan' && (
          <CommentInput
            rows={2}
            label="Negative Prompt"
            characters={formData.characterList}
            text={shot.negative_prompt || ''}
            placeholder="Write your negative prompt..."
            disabled={isVideoGenerating}
            onChange={value =>
              updateShotDataRef.current(
                sceneId,
                shot.id,
                'negative_prompt',
                value
              )
            }
          />
        )}
        <div
          className="card-actions"
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between'
          }}
        >
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu className="video-generation-menu">
                <Menu.Item
                  onClick={() => handleRegenerateVideo(sceneId, shot.id)}
                >
                  <ReloadOutlined /> Regenerate Video
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    document
                      .getElementById(`upload-input-${sceneId}-${shot.id}`)
                      .click()
                  }
                >
                  <UploadOutlined /> Upload Your Own Video
                  <input
                    id={`upload-input-${sceneId}-${shot.id}`}
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={e => {
                      const file = e.target.files[0]
                      if (file) handleUploadVideo(sceneId, shot.id, file)
                      e.target.value = null
                    }}
                  />
                </Menu.Item>
              </Menu>
            }
            disabled={isVideoGenerating}
            placement="bottomRight"
          >
            <Button icon="reload">
              Regenerate Video <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            disabled={isVideoGenerating}
            onClick={() => handleEditSettings(sceneId, shot.id)}
          >
            <SettingOutlined /> Clip Editor
          </Button>
        </div>
      </div>

      {!isLastShot && (
        <div className="transition-wrapper">
          <Popover
            content={
              <TransitionSelector
                onSelect={type =>
                  handleTransitionSelect(sceneId, shot.id, type)
                }
                currentTransition={transitions[`${sceneId}-${shot.id}`]}
              />
            }
            trigger="hover"
            placement="right"
          >
            <div className="transition-trigger">
              <TransitionDisplay
                type={transitions[`${sceneId}-${shot.id}`]}
                onRemove={() => handleTransitionSelect(sceneId, shot.id, null)}
              />
            </div>
          </Popover>
        </div>
      )}

      <VideoHistoryModal
        visible={historyModalVisible}
        history={shot.videos_history || []}
        onCancel={() => setHistoryModalVisible(false)}
        onSelect={url => {
          updateShotDataRef.current(sceneId, shot.id, 'vidSrc', url)
          setHistoryModalVisible(false)
        }}
      />
    </React.Fragment>
  )
}

export default Shot
